/* @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react'
import { keyframes } from '@emotion/react'

// Mirrors the AI-streaming cadence used by the hero (see AgentRow STREAM_CHAR_MS).
const DEFAULT_CADENCE_MS = 22

const caretBlink = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0; }
`

type StreamingTextProps = {
  // Plain string to type out, char-by-char. Pre-resolve i18n on the caller side
  // (e.g. via `intl.formatMessage({...})`) so message extraction still works.
  text: string
  // Gate the stream from the parent. When false the text stays empty; flipping
  // to true starts the type-out from char 0. Flipping back to false resets.
  // Default true (stream immediately).
  play?: boolean
  // Milliseconds between each character append. Lower = faster type-out.
  cadenceMs?: number
  // Show the blinking caret while streaming.
  caret?: boolean
  className?: string
  style?: React.CSSProperties
}

export function StreamingText({
  text,
  play = true,
  cadenceMs = DEFAULT_CADENCE_MS,
  caret = true,
  className,
  style,
}: StreamingTextProps) {
  const [visibleChars, setVisibleChars] = useState(0)

  useEffect(() => {
    if (!play) {
      setVisibleChars(0)
      return
    }
    setVisibleChars(0)
    const id = setInterval(() => {
      setVisibleChars((n) => {
        if (n >= text.length) {
          clearInterval(id)
          return n
        }
        return n + 1
      })
    }, cadenceMs)
    return () => clearInterval(id)
  }, [play, text, cadenceMs])

  const isStreaming = play && visibleChars < text.length
  const displayedText = play ? text.slice(0, visibleChars) : ''

  return (
    <span className={className} style={style}>
      {displayedText}
      {caret && isStreaming && (
        <span
          aria-hidden
          css={{
            display: 'inline-block',
            width: 2,
            height: '0.95em',
            verticalAlign: '-0.1em',
            marginLeft: 2,
            backgroundColor: 'currentColor',
            animation: `${caretBlink} 0.9s infinite`,
          }}
        />
      )}
    </span>
  )
}
